import type { ReactNode } from "react";
import React from "react";

import type { TextSize } from "constants/WidgetConstants";
import { countOccurrences } from "workers/Evaluation/helpers";

import { ValidationTypes } from "constants/WidgetValidation";
import type { DerivedPropertiesMap } from "utils/WidgetFactory";

import WidgetStyleContainer from "components/designSystems/appsmith/WidgetStyleContainer";
import type { Color } from "constants/Colors";
import type { Stylesheet } from "entities/AppTheming";
import { pick } from "lodash";
import { AutocompleteDataType } from "utils/autocomplete/AutocompleteDataType";
import type { WidgetProps, WidgetState } from "widgets/BaseWidget";
import BaseWidget from "widgets/BaseWidget";
import type { ContainerStyle } from "widgets/ContainerWidget/component";
import type { TextAlign } from "../component";
import TextComponent from "../component";
import { OverflowTypes } from "../constants";
import { DefaultAutocompleteDefinitions } from "widgets/WidgetUtils";
import type { AutocompletionDefinitions } from "widgets/constants";
import { EventType } from "constants/AppsmithActionConstants/ActionConstants";
// import {DateFormatOptions} from "../../DatePickerWidget2/widget/constants";
const MAX_HTML_PARSING_LENGTH = 1000;
class TextWidget extends BaseWidget<TextWidgetProps, WidgetState> {
  static getPropertyPaneEventConfig() {
    return super.getWidgetEvents('ClockWidget');
  }
  static getPropertyPaneContentConfig() {
    return [
      {
        sectionName: "基本",
        children: [{
          propertyName: "label",
          helpText: "Sets the text of the widget",
          label: "标签",
          controlType: "INPUT_TEXT",
          placeholderText: "",
          isBindProperty: true,
          isTriggerProperty: false,
          validation: {
            type: ValidationTypes.TEXT,
            params: { limitLineBreaks: true },
          },
        }]
      },
      {
        sectionName: "一般",
        children: [
          {
            propertyName: "isVisible",
            helpText: "Controls the visibility of the widget",
            label: "是否可见",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "isShowDate",
            helpText: "",
            label: "是否显示日期",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "dateType",
            label: "日期格式",
            controlType: "DROP_DOWN",
            options: [
              {
                label: "2024-12-10",
                value: "2024-12-10",
              },
              {
                label: "2024/12/10",
                value: "2024/12/10",
              },
              {
                label: "2024.12.10",
                value: "2024.12.10",
              },
              {
                label: "2024年12月10日",
                value: "2024年12月10日",
              },
              {
                label: "10-12-2024",
                value: "10-12-2024",
              },
              {
                label: "10/12/2024",
                value: "10/12/2024",
              },
              {
                label: "10.12.2024",
                value: "10.12.2024",
              },
            ],
            isJSConvertible: false,
            isBindProperty: false,
            isTriggerProperty: false,
            hidden: (props: any) => props.isShowDate == false,
            defaultValue: 'TEXT',
          },
          {
            propertyName: "isShowWeek",
            helpText: "",
            label: "是否显示星期",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "weekType",
            label: "时间格式",
            controlType: "DROP_DOWN",
            options: [
              {
                label: "星期二",
                value: "星期二",
              },
              {
                label: "周二",
                value: "周二",
              }
            ],
            isJSConvertible: false,
            isBindProperty: false,
            isTriggerProperty: false,
            hidden: (props: any) => props.isShowWeek == false,
            defaultValue: 'TEXT',
          },
          {
            propertyName: "isShowTime",
            helpText: "",
            label: "是否显示时间",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "timeType",
            label: "时间格式",
            controlType: "DROP_DOWN",
            options: [
              {
                label: "15:31:30",
                value: "15:31:30",
              },
              {
                label: "15:31",
                value: "15:31",
              }
            ],
            isJSConvertible: false,
            isBindProperty: false,
            isTriggerProperty: false,
            hidden: (props: any) => props.isShowTime == false,
            defaultValue: 'TEXT',
          },
          {
            propertyName: "sortType",
            label: "顺序",
            controlType: "DROP_DOWN",
            options: [
              {
                label: "日期星期时间",
                value: "日期星期时间",
              },
              {
                label: "日期时间星期",
                value: "日期时间星期",
              },
              {
                label: "星期日期时间",
                value: "星期日期时间",
              }
            ],
            isJSConvertible: false,
            isBindProperty: false,
            isTriggerProperty: false,
            defaultValue: 'TEXT',
          },
        ],
      },
    ];
  }

  static getStylesheetConfig(): Stylesheet {
    return {
      truncateButtonColor: "{{appsmith.theme.colors.primaryColor}}",
      fontFamily: "{{appsmith.theme.fontFamily.appFont}}",
      borderRadius: "{{appsmith.theme.borderRadius.appBorderRadius}}",
      textColor: "{{appsmith.theme.colors.textColor}}",
    };
  }

  static getPropertyPaneStyleConfig() {
    return [
      {
        sectionName: "一般",
        children: [
          {
            propertyName: "fontFamily",
            label: "字体",
            helpText: "Controls the font family being used",
            controlType: "DROP_DOWN",
            options: [
              {
                label: "System Default",
                value: "System Default",
              },
              {
                label: "Nunito Sans",
                value: "Nunito Sans",
              },
              {
                label: "Poppins",
                value: "Poppins",
              },
              {
                label: "Inter",
                value: "Inter",
              },
              {
                label: "Montserrat",
                value: "Montserrat",
              },
              {
                label: "Noto Sans",
                value: "Noto Sans",
              },
              {
                label: "Open Sans",
                value: "Open Sans",
              },
              {
                label: "Roboto",
                value: "Roboto",
              },
              {
                label: "Rubik",
                value: "Rubik",
              },
              {
                label: "Ubuntu",
                value: "Ubuntu",
              },
            ],
            defaultValue: "System Default",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
            },
          },
          {
            propertyName: "fontSize",
            label: "字体大小",
            helpText: "Controls the size of the font used",
            controlType: "DROP_DOWN",
            defaultValue: "1rem",
            options: [
              {
                label: "S",
                value: "0.875rem",
                subText: "0.875rem",
              },
              {
                label: "M",
                value: "1rem",
                subText: "1rem",
              },
              {
                label: "L",
                value: "1.25rem",
                subText: "1.25rem",
              },
              {
                label: "XL",
                value: "1.875rem",
                subText: "1.875rem",
              },
              {
                label: "XXL",
                value: "3rem",
                subText: "3rem",
              },
              {
                label: "3XL",
                value: "3.75rem",
                subText: "3.75rem",
              },
            ],
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
            },
          },
          {
            propertyName: "lineHeight",
            helpText: "Changes the width of the button",
            label: "行高",
            controlType: "INPUT_TEXT",
            isJSConvertible: false,
            isBindProperty: true,
            // isTriggerProperty: false,
            validation: { type: ValidationTypes.NUMBER },
          },
          {
            propertyName: "textAlign",
            label: "对齐方式",
            helpText: "Controls the horizontal alignment of the text",
            controlType: "ICON_TABS",
            fullWidth: true,
            options: [
              {
                startIcon: "align-left",
                value: "LEFT",
              },
              {
                startIcon: "align-center",
                value: "CENTER",
              },
              {
                startIcon: "align-right",
                value: "RIGHT",
              },
            ],
            defaultValue: "LEFT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
          {
            propertyName: "fontStyle",
            label: "加粗&斜体",
            helpText: "Controls the font emphasis of the text displayed",
            controlType: "BUTTON_GROUP",
            options: [
              {
                icon: "text-bold",
                value: "BOLD",
              },
              {
                icon: "text-italic",
                value: "ITALIC",
              },
            ],
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
        ],
      },
      {
        sectionName: "颜色",
        children: [
          {
            propertyName: "textColor",
            label: "字体颜色",
            helpText: "Controls the color of the text displayed",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
              params: {
                regex: /^(?![<|{{]).+/,
              },
            },
          },
          {
            propertyName: "backgroundColor",
            label: "背景颜色",
            helpText: "Background color of the text added",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
              params: {
                regex: /^((?![<|{{]).+){0,1}/,
                expected: {
                  type: "string (HTML color name or HEX value)",
                  example: `red | #9C0D38`,
                  autocompleteDataType: AutocompleteDataType.STRING,
                },
              },
            },
          },
          {
            helpText: "Use a html color name, HEX, RGB or RGBA value",
            placeholderText: "#FFFFFF / Gray / rgb(255, 99, 71)",
            propertyName: "borderColor",
            label: "边框颜色",
            controlType: "COLOR_PICKER",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
          {
            propertyName: "truncateButtonColor",
            label: "Truncate button color",
            helpText: "Controls the color of the truncate button",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
              params: {
                regex: /^((?![<|{{]).+){0,1}/,
              },
            },
            dependencies: ["overflow"],
            hidden: (props: TextWidgetProps) => {
              return props.overflow !== OverflowTypes.TRUNCATE;
            },
          },
        ],
      },
      {
        sectionName: "边框&阴影",
        children: [
          {
            helpText:
              "Enter value for border width which can also use as margin",
            propertyName: "borderWidth",
            label: "边框宽度",
            placeholderText: "Enter value in px",
            controlType: "INPUT_TEXT",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.NUMBER },
          },
        ],
      },
    ];
  }

  handleActionResult = (result: any) => { };
  // onClick = () => {
  //   if (this.props.onClick) {
  //     super.executeAction({
  //       triggerPropertyName: "onClick",
  //       dynamicString: this.props.onClick,
  //       event: {
  //         type: EventType.ON_CLICK,
  //         callback: this.handleActionResult,
  //       },
  //     });
  //   }
  // };

  getPageView() {
    // const disableLink: boolean = this.props.disableLink
    //   ? true
    //   : this.shouldDisableLink();
    return (
      <WidgetStyleContainer
        accentColor="transparent"
        className="t--text-widget-container"
        {...pick(this.props, [
          "widgetId",
          "containerStyle",
          "borderColor",
          "borderWidth",
        ])}
      >
        <TextComponent
          accentColor={this.props.accentColor}
          backgroundColor={this.props.backgroundColor}
          lineHeight={this.props.lineHeight}
          bottomRow={this.props.bottomRow}

          fontFamily={this.props.fontFamily}
          fontSize={this.props.fontSize}
          fontStyle={this.props.fontStyle}
          isLoading={this.props.isLoading}
          key={this.props.widgetId}
          leftColumn={this.props.leftColumn}
          overflow={this.props.overflow}
          rightColumn={this.props.rightColumn}
          text={this.props.text}

          textAlign={this.props.textAlign ? this.props.textAlign : "LEFT"}
          textColor={this.props.textColor}
          textType={this.props.textType}
          topRow={this.props.topRow}
          truncateButtonColor={
            this.props.truncateButtonColor || this.props.accentColor
          }
          widgetId={this.props.widgetId}
          isShowDate={this.props.isShowDate}
          dateType={this.props.dateType}
          isShowTime={this.props.isShowTime}
          timeType={this.props.timeType}
          isShowWeek={this.props.isShowWeek}
          weekType={this.props.weekType}
          sortType={this.props.sortType}
        />
      </WidgetStyleContainer>
    );
  }

  static getDerivedPropertiesMap(): DerivedPropertiesMap {
    return {
      value: `{{ this.text }}`,
      getValue: `{{ this.text }}`,
    };
  }

  static getAutocompleteDefinitions(): AutocompletionDefinitions {
    return {
      "!doc":
        "‌Text widget is used to display textual information. Whether you want to display a paragraph or information or add a heading to a container, a text widget makes it easy to style and display text",
      "!url": "https://docs.appsmith.com/widget-reference/text",
      isVisible: DefaultAutocompleteDefinitions.isVisible,
      text: "string",
    };
  }

  static getWidgetType() {
    return "CLOCK_WIDGET";
  }
}

export interface TextStyles {
  backgroundColor?: string;
  textColor?: string;
  fontStyle?: string;
  fontSize?: TextSize;
  textAlign?: TextAlign;
  truncateButtonColor?: string;
  fontFamily: string;
  lineHeight?: string;
  isShowDate?: Boolean;
  dateType?: string;
  isShowTime?: Boolean;
  timeType?: string;
  isShowWeek?: Boolean;
  weekType?: string;
  sortType?: string;
}

export interface TextWidgetProps extends WidgetProps, TextStyles {
  accentColor: string;
  text?: string;
  isLoading: boolean;
  widgetId: string;
  containerStyle?: ContainerStyle;
  children?: ReactNode;
  borderColor?: Color;
  borderWidth?: number;
  overflow: OverflowTypes;
  //onClick?: () => void;
  onClick: string;
  dateType?: string;
  isShowTime?: Boolean;
  timeType?: string;
  isShowWeek?: Boolean;
  weekType?: string;
  sortType?: string;
}

export default TextWidget;
